import React, { useState } from "react";
import axios from "axios";
import loginImg from "../images/scooterlab.jpg";
import { Link } from "react-router-dom";
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  Grid,
  Divider,
  InputAdornment,
  IconButton,
  Avatar,
  Snackbar,
  Alert
} from "@mui/material";
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff
} from "@mui/icons-material";
import { deepPurple } from "@mui/material/colors";
import { useAuth } from "../services/auth/AuthContext";
import { useNavigate } from "react-router-dom";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError("Please enter both email and password");
      return;
    }
  
    try {
      const result = await login(email, password);
      // If 2FA required, route to verify screen with email
      if (result && result.requires_2fa) {
        navigate(`/verify-code?email=${encodeURIComponent(result.email)}`, { state: { email: result.email } });
        return;
      }
      
      if (result && result.success) {
        navigate('/dashboard');
      } else {
        setError("Login failed. Please try again.");
      }
    } catch (err) {
      console.log("Login error:", err);
      // Simplified error handling to show backend error messages properly
      setError(err.message || "Login failed. Please try again.");
    }
  };

  const handleCloseSnackbar = () => {
    setError(null);
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Container maxWidth="lg" sx={{ height: "100vh", p: 0 }}>
      <Grid container component={Paper} elevation={6} sx={{ height: "100%", borderRadius: 0 }}>
        {/* Left Side - Image Section */}
        <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'flex' } }}>
          <Box
            sx={{
              width: "100%",
              height: "100%",
              position: "relative",
              overflow: "hidden"
            }}
          >
            <img
              src={loginImg}
              alt="ScooterLab Welcome"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover"
              }}
            />
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(0, 0, 0, 0.3)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                color: "white",
                p: 4,
                textAlign: "center"
              }}
            >
              <Typography variant="h4" sx={{ fontWeight: "bold", mb: 2 }}>
                Welcome to ScooterLab
              </Typography>
              <Typography variant="h6" sx={{ mb: 4 }}>
                Your Gateway to Smarter Mobility
              </Typography>
              <Divider sx={{ width: '80%', my: 2, bgcolor: 'rgba(255,255,255,0.5)' }} />
            </Box>
          </Box>
        </Grid>

        {/* Right Side - Login Form */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              p: { xs: 4, sm: 8 }
            }}
          >
            <Avatar sx={{ 
              bgcolor: deepPurple[500], 
              mb: 3,
              width: 60,
              height: 60
            }}>
              <LockIcon />
            </Avatar>

            <Typography variant="h4" component="h1" sx={{ mb: 1, fontWeight: "bold" }}>
              Sign In
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Enter your details to access your account
            </Typography>

            <Box component="form" onSubmit={handleLogin} sx={{ width: "100%", maxWidth: 400 }}>
              <TextField
                fullWidth
                label="Email Address"
                variant="outlined"
                margin="normal"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Password"
                variant="outlined"
                margin="normal"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleClickShowPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  mt: 2,
                  mb: 2,
                  py: 1.5,
                  bgcolor: deepPurple[600],
                  '&:hover': { bgcolor: deepPurple[800] },
                }}
              >
                Login
              </Button>

              <Grid container justifyContent="space-between">
                <Grid item>
                </Grid>
                <Grid item>
                  <Link to="/forgot-password" variant="body2" style={{ color: deepPurple[600], textDecoration: 'none' }}>
                    Forgot password?
                  </Link>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Grid>
      </Grid>
      <Snackbar
        open={!!error}
        autoHideDuration={2500}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Login;